/**
 * Resolve the backend base URL with a runtime safety net.
 *
 * Wave 29 Chunk 10k — Production "login loop" bug 2026-05-12:
 *
 *   The frontend bundle is built ONCE per deploy, with whatever sits
 *   in `frontend/.env` at build time. That file points at the preview
 *   URL. When Emergent ships that bundle to a custom-domain production
 *   deploy (e.g. salesflow.hidroverse.co.za), every API call goes to
 *   the PREVIEW backend → CORS rejects it → login loops.
 *
 * This pure resolver detects the host mismatch and self-corrects to
 * same-origin so a single frontend bundle is safe to deploy to ANY
 * host without ever needing to edit env vars.
 *
 * Pulled into its own module so jest can unit-test it without having
 * to load `api.js` (which imports axios + offlineQueue and trips the
 * jest CJS/ESM bridge).
 *
 * @param {string|null|undefined} bakedEnv  - the build-time REACT_APP_BACKEND_URL
 * @param {{ origin: string } | null}  loc  - typically window.location; pass null in non-browser
 * @returns {string}                          a usable backend base URL (no trailing slash)
 */
export function resolveBackendUrl(bakedEnv, loc) {
  const baked = (bakedEnv || "").trim();
  if (!loc || !loc.origin) return baked;
  const origin = loc.origin;
  // Localhost dev → trust the env var; backend is usually on a different port.
  if (/^https?:\/\/localhost(:\d+)?$/i.test(origin) || /^https?:\/\/127\.0\.0\.1(:\d+)?$/i.test(origin)) {
    return baked || origin;
  }
  if (!baked) return origin;
  try {
    const bakedHost = new URL(baked).host;
    const originHost = new URL(origin).host;
    if (bakedHost.toLowerCase() === originHost.toLowerCase()) return baked;
    // Mismatch → same-origin wins. Log so devs see it during build verification.
    // eslint-disable-next-line no-console
    console.warn(
      "[api] REACT_APP_BACKEND_URL host (" + bakedHost +
      ") doesn't match window.location.origin host (" + originHost +
      "). Auto-correcting to same-origin to avoid CORS.",
    );
    return origin;
  } catch (e) {
    // Malformed URL in either input → return whatever's likely usable.
    return baked || origin;
  }
}
